/**
 * AntarDristi — Client-Side Vedic Astrology Engine
 *
 * Runs entirely in the browser. Uses astronomy-engine (VSOP87/ELP)
 * for precise geocentric ecliptic longitudes. No server call needed.
 *
 * Ported from vedic-astrology-api's core logic with our Dasha/grid additions.
 */

import * as Astronomy from 'astronomy-engine';

// ─── Reference Data ───────────────────────────────────────────────

const SIGNS = [
  'Mesha', 'Vrishabha', 'Mithuna', 'Kataka',
  'Simha', 'Kanya', 'Tula', 'Vrischika',
  'Dhanu', 'Makara', 'Kumbha', 'Meena'
];

const SIGNS_EN = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_ABBR = [
  'Mes', 'Vri', 'Mit', 'Kat',
  'Sim', 'Kan', 'Tul', 'Vrs',
  'Dhn', 'Mak', 'Kum', 'Mee'
];

const SIGN_LORDS = [
  'Mars', 'Venus', 'Mercury', 'Moon',
  'Sun', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Saturn', 'Jupiter'
];

const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira',
  'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha', 'Magha',
  'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati',
  'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha',
  'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];

const NAKSHATRA_LORDS = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars',
  'Rahu', 'Jupiter', 'Saturn', 'Mercury', 'Ketu',
  'Venus', 'Sun', 'Moon', 'Mars', 'Rahu',
  'Jupiter', 'Saturn', 'Mercury', 'Ketu', 'Venus',
  'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter',
  'Saturn', 'Mercury'
];

const DASHA_YEARS = {
  'Ketu': 7, 'Venus': 20, 'Sun': 6, 'Moon': 10, 'Mars': 7,
  'Rahu': 18, 'Jupiter': 16, 'Saturn': 19, 'Mercury': 17
};

const DASHA_SEQUENCE = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars',
  'Rahu', 'Jupiter', 'Saturn', 'Mercury'
];

const PLANET_SYMBOLS = {
  'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma',
  'Mercury': 'Me', 'Jupiter': 'Ju', 'Venus': 'Ve',
  'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke', 'Lagna': 'Lg'
};

// Planetary dignity — Uchcha (exaltation), Neecha (debilitation),
// Swakshetra (own sign), Moolatrikona
// Sign indices: 0=Mesha, 1=Vrishabha, ... 11=Meena
const PLANET_DIGNITY = {
  Sun:     { uchcha: 0,  neecha: 6,  own: [4],       moola: 4  },  // Exalted Mesha, Debilitated Tula, Own Simha, Moola Simha
  Moon:    { uchcha: 1,  neecha: 7,  own: [3],       moola: 1  },  // Exalted Vrishabha, Debilitated Vrischika, Own Kataka
  Mars:    { uchcha: 9,  neecha: 3,  own: [0, 7],    moola: 0  },  // Exalted Makara, Debilitated Kataka, Own Mesha+Vrischika
  Mercury: { uchcha: 5,  neecha: 11, own: [2, 5],    moola: 5  },  // Exalted Kanya, Debilitated Meena, Own Mithuna+Kanya
  Jupiter: { uchcha: 3,  neecha: 9,  own: [8, 11],   moola: 8  },  // Exalted Kataka, Debilitated Makara, Own Dhanu+Meena
  Venus:   { uchcha: 11, neecha: 5,  own: [1, 6],    moola: 6  },  // Exalted Meena, Debilitated Kanya, Own Vrishabha+Tula
  Saturn:  { uchcha: 6,  neecha: 0,  own: [9, 10],   moola: 10 },  // Exalted Tula, Debilitated Mesha, Own Makara+Kumbha
  Rahu:    { uchcha: 1,  neecha: 7,  own: [10],      moola: null }, // Uchcha Vrishabha (traditional), Neecha Vrischika
  Ketu:    { uchcha: 7,  neecha: 1,  own: [7],       moola: null }, // Uchcha Vrischika, Neecha Vrishabha
};

/**
 * Get planetary dignity status for a planet in a given sign.
 * Returns: { status, label, color }
 */
export function getPlanetDignity(planetName, signIndex) {
  const dig = PLANET_DIGNITY[planetName];
  if (!dig) return { status: 'neutral', label: '', color: '' };

  if (signIndex === dig.uchcha) return { status: 'uchcha', label: 'Uchcha (Exalted)', shortLabel: 'Uchcha', color: '#2a8a4a' };
  if (signIndex === dig.neecha) return { status: 'neecha', label: 'Neecha (Debilitated)', shortLabel: 'Neecha', color: '#b44' };
  if (signIndex === dig.moola) return { status: 'moolatrikona', label: 'Moolatrikona', shortLabel: 'Moola', color: '#6a5acd' };
  if (dig.own.includes(signIndex)) return { status: 'sva', label: 'Sva Kshetra (Own Sign)', shortLabel: 'Sva', color: '#2a6a8a' };

  return { status: 'neutral', label: '', shortLabel: '', color: '' };
}

// Naisargika Maitri — Natural Planetary Friendships
// Based on Parashari system
const PLANET_RELATIONSHIPS = {
  Sun:     { friends: ['Moon', 'Mars', 'Jupiter'],           enemies: ['Venus', 'Saturn'],              neutral: ['Mercury'] },
  Moon:    { friends: ['Sun', 'Mercury'],                    enemies: [],                                neutral: ['Mars', 'Jupiter', 'Venus', 'Saturn'] },
  Mars:    { friends: ['Sun', 'Moon', 'Jupiter'],            enemies: ['Mercury'],                       neutral: ['Venus', 'Saturn'] },
  Mercury: { friends: ['Sun', 'Venus'],                      enemies: ['Moon'],                          neutral: ['Mars', 'Jupiter', 'Saturn'] },
  Jupiter: { friends: ['Sun', 'Moon', 'Mars'],               enemies: ['Mercury', 'Venus'],              neutral: ['Saturn'] },
  Venus:   { friends: ['Mercury', 'Saturn'],                 enemies: ['Sun', 'Moon'],                   neutral: ['Mars', 'Jupiter'] },
  Saturn:  { friends: ['Mercury', 'Venus'],                  enemies: ['Sun', 'Moon', 'Mars'],           neutral: ['Jupiter'] },
  Rahu:    { friends: ['Mercury', 'Venus', 'Saturn'],        enemies: ['Sun', 'Moon', 'Mars'],           neutral: ['Jupiter'] },
  Ketu:    { friends: ['Mars', 'Jupiter'],                   enemies: ['Mercury', 'Venus'],              neutral: ['Sun', 'Moon', 'Saturn'] },
};

// Favorable Gochara transit houses (counted from Moon sign / Janma Rashi)
// Traditional Vedic transit results
const GOCHARA_FAVORABLE = {
  Sun:     { good: [3, 6, 10, 11],       bad: [1, 2, 4, 5, 7, 8, 9, 12] },
  Moon:    { good: [1, 3, 6, 7, 10, 11], bad: [2, 4, 5, 8, 9, 12] },
  Mars:    { good: [3, 6, 11],           bad: [1, 2, 4, 5, 7, 8, 9, 10, 12] },
  Mercury: { good: [2, 4, 6, 8, 10, 11], bad: [1, 3, 5, 7, 9, 12] },
  Jupiter: { good: [2, 5, 7, 9, 11],     bad: [1, 3, 4, 6, 8, 10, 12] },
  Venus:   { good: [1, 2, 3, 4, 5, 8, 9, 11, 12], bad: [6, 7, 10] },
  Saturn:  { good: [3, 6, 11],           bad: [1, 2, 4, 5, 7, 8, 9, 10, 12] },
  Rahu:    { good: [3, 6, 10, 11],       bad: [1, 2, 4, 5, 7, 8, 9, 12] },
  Ketu:    { good: [3, 6, 11],           bad: [1, 2, 4, 5, 7, 8, 9, 10, 12] },
};

export function getPlanetRelationships(planetName) {
  return PLANET_RELATIONSHIPS[planetName] || { friends: [], enemies: [], neutral: [] };
}

export function getGocharaInfo(planetName) {
  return GOCHARA_FAVORABLE[planetName] || { good: [], bad: [] };
}

/**
 * Get the gochara house number of a transit planet relative to the Moon sign (Janma Rashi).
 * House 1 = Moon's sign, House 2 = next sign, etc.
 */
export function getGocharaHouse(transitSignIndex, moonSignIndex) {
  return ((transitSignIndex - moonSignIndex + 12) % 12) + 1;
}

/**
 * Check if a transit is favorable based on traditional gochara rules.
 */
/**
 * Get nakshatras that fall within a given rashi (sign index 0-11).
 * Each rashi spans 30°. Each nakshatra spans 13°20' (13.333°) with 4 padas of 3°20' each.
 * Returns array of { name, padas: [which padas fall in this sign] }
 */
export function getNakshatrasInSign(signIndex) {
  const signStart = signIndex * 30;
  const signEnd = signStart + 30;
  const nakSpan = 360 / 27; // 13.333°
  const padaSpan = nakSpan / 4; // 3.333°
  const result = [];

  for (let n = 0; n < 27; n++) {
    const nakStart = n * nakSpan;
    const nakEnd = nakStart + nakSpan;
    // Check if this nakshatra overlaps with the sign
    if (nakEnd > signStart && nakStart < signEnd) {
      const padas = [];
      for (let p = 0; p < 4; p++) {
        const padaStart = nakStart + p * padaSpan;
        const padaEnd = padaStart + padaSpan;
        if (padaEnd > signStart && padaStart < signEnd) {
          padas.push(p + 1);
        }
      }
      if (padas.length > 0) {
        result.push({ name: NAKSHATRAS[n], lord: NAKSHATRA_LORDS[n], padas });
      }
    }
  }
  return result;
}

// Average transit durations per sign (approximate days)
const TRANSIT_DURATIONS = {
  Sun:     { perSign: 30,   cycle: '1 year',     desc: '~30 days per Rashi' },
  Moon:    { perSign: 2.25, cycle: '27.3 days',  desc: '~2.25 days per Rashi' },
  Mars:    { perSign: 45,   cycle: '1.9 years',  desc: '~45 days per Rashi (varies 28-60+)' },
  Mercury: { perSign: 25,   cycle: '1 year',     desc: '~25 days per Rashi (with retro loops)' },
  Jupiter: { perSign: 365,  cycle: '12 years',   desc: '~1 year per Rashi' },
  Venus:   { perSign: 28,   cycle: '1 year',     desc: '~28 days per Rashi (varies with retro)' },
  Saturn:  { perSign: 912,  cycle: '29.5 years',  desc: '~2.5 years per Rashi' },
  Rahu:    { perSign: 547,  cycle: '18 years',   desc: '~1.5 years per Rashi (retrograde)' },
  Ketu:    { perSign: 547,  cycle: '18 years',   desc: '~1.5 years per Rashi (retrograde)' },
};

/**
 * Get detailed transit info for a planet — current position, when it entered
 * and when it will leave the current sign, by scanning positions day by day.
 */
export function getTransitTimeline(planetName) {
  const now = new Date();
  const ayanamsa = getLahiriAyanamsa(now);
  const toSid = (trop) => norm360(trop - ayanamsa);

  const getSidLon = (date) => {
    if (planetName === 'Rahu' || planetName === 'Ketu') {
      const ayan = getLahiriAyanamsa(date);
      const nodes = calculateLunarNodes(date, ayan);
      return nodes[planetName].longitude;
    }
    const tropLon = getGeocentricEclipticLon(planetName, date);
    return norm360(tropLon - getLahiriAyanamsa(date));
  };

  const currentLon = getSidLon(now);
  const currentSign = getSignIndex(currentLon);
  const currentNak = getNakshatra(currentLon);
  const duration = TRANSIT_DURATIONS[planetName] || { perSign: 30, cycle: '—', desc: '—' };

  // Scan backward to find sign entry date (max 2 years back)
  let entryDate = null;
  const maxBackDays = Math.min(duration.perSign * 2, 1000);
  const stepBack = planetName === 'Moon' ? 0.25 : 1; // Moon moves fast
  for (let d = stepBack; d <= maxBackDays; d += stepBack) {
    const pastDate = new Date(now.getTime() - d * 86400000);
    const pastSign = getSignIndex(getSidLon(pastDate));
    if (pastSign !== currentSign) {
      // Refine to nearest half-day
      const refineDate = new Date(now.getTime() - (d - stepBack) * 86400000);
      entryDate = refineDate;
      break;
    }
  }

  // Scan forward to find sign exit date (max 3 years forward)
  let exitDate = null;
  const maxFwdDays = Math.min(duration.perSign * 3, 1200);
  const stepFwd = planetName === 'Moon' ? 0.25 : 1;
  for (let d = stepFwd; d <= maxFwdDays; d += stepFwd) {
    const futureDate = new Date(now.getTime() + d * 86400000);
    const futureSign = getSignIndex(getSidLon(futureDate));
    if (futureSign !== currentSign) {
      exitDate = futureDate;
      break;
    }
  }

  const formatDate = (dt) => dt ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}` : '—';

  // Nakshatras in current sign
  const naksInSign = getNakshatrasInSign(currentSign);

  // Next rashi info
  const nextSignIdx = (planetName === 'Rahu' || planetName === 'Ketu')
    ? (currentSign + 11) % 12  // Rahu/Ketu move retrograde
    : (currentSign + 1) % 12;
  const nextSignLord = SIGN_LORDS[nextSignIdx];
  // Is the next sign lord a friend or enemy of this planet?
  const rels = PLANET_RELATIONSHIPS[planetName] || { friends: [], enemies: [], neutral: [] };
  let nextSignRelation = 'neutral';
  if (rels.friends.includes(nextSignLord)) nextSignRelation = 'friend';
  else if (rels.enemies.includes(nextSignLord)) nextSignRelation = 'enemy';
  // Dignity in next sign
  const nextDignity = getPlanetDignity(planetName, nextSignIdx);
  // First nakshatra in next sign
  const naksInNextSign = getNakshatrasInSign(nextSignIdx);

  return {
    planet: planetName,
    symbol: PLANET_SYMBOLS[planetName],
    longitude: round2(currentLon),
    degree: round2(currentLon % 30),
    sign: SIGNS[currentSign],
    signEn: SIGNS_EN[currentSign],
    signIndex: currentSign,
    signLord: SIGN_LORDS[currentSign],
    nakshatra: currentNak.name,
    nakshatraLord: currentNak.lord,
    pada: currentNak.pada,
    entryDate: formatDate(entryDate),
    exitDate: formatDate(exitDate),
    transitDuration: duration.desc,
    cycle: duration.cycle,
    isRetrograde: (planetName !== 'Sun' && planetName !== 'Moon' && planetName !== 'Rahu' && planetName !== 'Ketu')
      ? isRetrograde(planetName, now) : (planetName === 'Rahu' || planetName === 'Ketu'),
    naksInSign,
    next: {
      sign: SIGNS[nextSignIdx],
      signEn: SIGNS_EN[nextSignIdx],
      signIndex: nextSignIdx,
      signLord: nextSignLord,
      relation: nextSignRelation,
      dignity: nextDignity,
      firstNakshatra: naksInNextSign.length > 0 ? naksInNextSign[0].name : '—',
      naksInSign: naksInNextSign,
    }
  };
}

/**
 * Generate a Gochara forecast — upcoming sign transits for all planets.
 * Returns an array of monthly snapshots showing where each planet will be.
 */
export function getGocharaForecast(moonSignIndex, monthsAhead = 12, monthsBehind = 12) {
  const forecast = [];
  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  const now = new Date();

  for (let m = -monthsBehind; m <= monthsAhead; m++) {
    const date = new Date(now.getFullYear(), now.getMonth() + m, 15); // mid-month sample
    const ayanamsa = getLahiriAyanamsa(date);

    const monthLabel = date.toLocaleString('en', { month: 'short', year: 'numeric' });
    const entry = { month: monthLabel, date, planets: {} };

    for (const p of planets) {
      let sidLon;
      if (p === 'Rahu' || p === 'Ketu') {
        const nodes = calculateLunarNodes(date, ayanamsa);
        sidLon = nodes[p].longitude;
      } else {
        const tropLon = getGeocentricEclipticLon(p, date);
        sidLon = norm360(tropLon - ayanamsa);
      }

      const si = getSignIndex(sidLon);
      const nak = getNakshatra(sidLon);
      const gocharaHouse = moonSignIndex != null ? ((si - moonSignIndex + 12) % 12) + 1 : null;
      const fav = gocharaHouse != null ? (GOCHARA_FAVORABLE[p]?.good.includes(gocharaHouse) ? 'favorable' : GOCHARA_FAVORABLE[p]?.bad.includes(gocharaHouse) ? 'unfavorable' : 'neutral') : null;
      const dignity = getPlanetDignity(p, si);
      const rels = PLANET_RELATIONSHIPS[p] || { friends: [], enemies: [], neutral: [] };
      const signLord = SIGN_LORDS[si];
      let lordRelation = 'neutral';
      if (rels.friends.includes(signLord)) lordRelation = 'friend';
      else if (rels.enemies.includes(signLord)) lordRelation = 'enemy';

      entry.planets[p] = {
        sign: SIGNS[si], signEn: SIGNS_EN[si], signIndex: si,
        degree: round2(sidLon % 30),
        nakshatra: nak.name, pada: nak.pada,
        gocharaHouse, favorability: fav,
        dignity, lordRelation,
        symbol: PLANET_SYMBOLS[p]
      };
    }

    forecast.push(entry);
  }

  return forecast;
}

export function isGocharaFavorable(planetName, gocharaHouse) {
  const info = GOCHARA_FAVORABLE[planetName];
  if (!info) return null;
  if (info.good.includes(gocharaHouse)) return 'favorable';
  if (info.bad.includes(gocharaHouse)) return 'unfavorable';
  return 'neutral';
}

const SOUTH_INDIAN_GRID = [
  { sign: 11, row: 0, col: 0 },
  { sign: 0,  row: 0, col: 1 },
  { sign: 1,  row: 0, col: 2 },
  { sign: 2,  row: 0, col: 3 },
  { sign: 10, row: 1, col: 0 },
  { sign: 3,  row: 1, col: 3 },
  { sign: 9,  row: 2, col: 0 },
  { sign: 4,  row: 2, col: 3 },
  { sign: 8,  row: 3, col: 0 },
  { sign: 7,  row: 3, col: 1 },
  { sign: 6,  row: 3, col: 2 },
  { sign: 5,  row: 3, col: 3 },
];

// ─── Utility ──────────────────────────────────────────────────────

function norm360(d) { return ((d % 360) + 360) % 360; }
function round2(n) { return Math.round(n * 100) / 100; }
function getSignIndex(lon) { return Math.floor(norm360(lon) / 30); }

// Sign properties used by varga (divisional chart) rules.
// Quality: 0=movable (cardinal), 1=fixed, 2=dual (mutable). Aries=movable, Taurus=fixed, Gemini=dual, Cancer=movable, ...
// Element: 0=fire, 1=earth, 2=air, 3=water. Aries=fire, Taurus=earth, Gemini=air, Cancer=water, repeating.
// Odd/even sign: Aries(0)=odd, Taurus(1)=even, Gemini(2)=odd, ... 0-indexed even-index = "odd sign" (1st, 3rd, 5th... in 1-based count).
function getSignQuality(si) { return si % 3; }
function getSignElement(si) { return si % 4; }
function isOddSign(si) { return si % 2 === 0; }

/**
 * Returns the divisional sign index (0-11) for a sidereal longitude under the given Shodashavarga divisor.
 * Implements classical Parashari rules for D1, D2, D3, D4, D7, D9, D10, D12, D16, D20, D24, D27, D30, D40, D45, D60.
 */
export function getDivisionalSign(lon, divisor) {
  const L = norm360(lon);
  const si = Math.floor(L / 30);
  const deg = L - si * 30;
  switch (divisor) {
    case 1: return si;
    case 2: { // Hora — Sun's & Moon's halves of each sign
      const half = Math.floor(deg / 15);
      if (isOddSign(si)) return half === 0 ? 4 : 3; // odd: 1st half Leo, 2nd half Cancer
      return half === 0 ? 3 : 4;                    // even: 1st half Cancer, 2nd half Leo
    }
    case 3: { // Drekkana — 10° each, starts from same / 5th / 9th from sign
      const part = Math.floor(deg / 10);
      return (si + part * 4) % 12;
    }
    case 4: { // Chaturthamsa — 7°30' each, starts from 1st / 4th / 7th / 10th
      const part = Math.floor(deg / 7.5);
      return (si + part * 3) % 12;
    }
    case 7: { // Saptamsa — 30/7 each, odd from same, even from 7th
      const part = Math.floor(deg / (30 / 7));
      const start = isOddSign(si) ? si : (si + 6) % 12;
      return (start + part) % 12;
    }
    case 9: return Math.floor(L * 9 / 30) % 12; // Navamsa — unified formula
    case 10: { // Dasamsa — 3° each, odd from same, even from 9th
      const part = Math.floor(deg / 3);
      const start = isOddSign(si) ? si : (si + 8) % 12;
      return (start + part) % 12;
    }
    case 12: { // Dvadasamsa — 2°30' each, starts from same sign
      const part = Math.floor(deg / 2.5);
      return (si + part) % 12;
    }
    case 16: { // Shodasamsa — 30/16 each. Movable→Aries, Fixed→Leo, Dual→Sagittarius
      const part = Math.floor(deg / (30 / 16));
      const q = getSignQuality(si);
      const start = q === 0 ? 0 : q === 1 ? 4 : 8;
      return (start + part) % 12;
    }
    case 20: { // Vimsamsa — 1°30' each. Movable→Aries, Fixed→Sagittarius, Dual→Leo
      const part = Math.floor(deg / 1.5);
      const q = getSignQuality(si);
      const start = q === 0 ? 0 : q === 1 ? 8 : 4;
      return (start + part) % 12;
    }
    case 24: { // Chaturvimsamsa (Siddhamsa) — 1°15' each. Odd from Leo, Even from Cancer
      const part = Math.floor(deg / 1.25);
      const start = isOddSign(si) ? 4 : 3;
      return (start + part) % 12;
    }
    case 27: { // Bhamsa (Saptavimsamsa) — 30/27 each. Fire→Aries, Earth→Cancer, Air→Libra, Water→Capricorn
      const part = Math.floor(deg / (30 / 27));
      const e = getSignElement(si);
      const start = e === 0 ? 0 : e === 1 ? 3 : e === 2 ? 6 : 9;
      return (start + part) % 12;
    }
    case 30: { // Trimsamsa — 5 unequal portions ruled by Mars/Saturn/Jupiter/Mercury/Venus
      if (isOddSign(si)) {
        if (deg < 5)  return 0;   // Mars → Aries
        if (deg < 10) return 10;  // Saturn → Aquarius
        if (deg < 18) return 8;   // Jupiter → Sagittarius
        if (deg < 25) return 2;   // Mercury → Gemini
        return 6;                 // Venus → Libra
      }
      if (deg < 5)  return 1;     // Venus → Taurus
      if (deg < 12) return 5;     // Mercury → Virgo
      if (deg < 20) return 11;    // Jupiter → Pisces
      if (deg < 25) return 9;     // Saturn → Capricorn
      return 7;                   // Mars → Scorpio
    }
    case 40: { // Khavedamsa — 0°45' each. Odd from Aries, Even from Libra
      const part = Math.floor(deg / 0.75);
      const start = isOddSign(si) ? 0 : 6;
      return (start + part) % 12;
    }
    case 45: { // Akshavedamsa — 30/45 each. Movable→Aries, Fixed→Leo, Dual→Sagittarius
      const part = Math.floor(deg / (30 / 45));
      const q = getSignQuality(si);
      const start = q === 0 ? 0 : q === 1 ? 4 : 8;
      return (start + part) % 12;
    }
    case 60: { // Shashtiamsa — 0°30' each, advances one sign per amsa
      const part = Math.floor(deg * 2);
      return (si + part) % 12;
    }
    default: return si;
  }
}

// Shodashavarga metadata for the UI dropdown.
export const VARGAS = [
  { divisor: 1,  name: 'Rasi',           short: 'D1',  area: 'Birth chart — body, life, the seen world' },
  { divisor: 2,  name: 'Hora',           short: 'D2',  area: 'Wealth, accumulated resources' },
  { divisor: 3,  name: 'Drekkana',       short: 'D3',  area: 'Siblings, courage, immediate environment' },
  { divisor: 4,  name: 'Chaturthamsa',   short: 'D4',  area: 'Fortune, fixed assets, home and land' },
  { divisor: 7,  name: 'Saptamsa',       short: 'D7',  area: 'Children, progeny, creative legacy' },
  { divisor: 9,  name: 'Navamsa',        short: 'D9',  area: 'Marriage, dharma, inner ripening of grahas' },
  { divisor: 10, name: 'Dasamsa',        short: 'D10', area: 'Career, profession, public standing' },
  { divisor: 12, name: 'Dvadasamsa',     short: 'D12', area: 'Parents, ancestry, lineage' },
  { divisor: 16, name: 'Shodasamsa',     short: 'D16', area: 'Vehicles, comforts, conveyances' },
  { divisor: 20, name: 'Vimsamsa',       short: 'D20', area: 'Spirituality, upasana, devotional practice' },
  { divisor: 24, name: 'Chaturvimsamsa', short: 'D24', area: 'Education, learning, scholastic skill' },
  { divisor: 27, name: 'Bhamsa',         short: 'D27', area: 'Strengths and weaknesses, stamina' },
  { divisor: 30, name: 'Trimsamsa',      short: 'D30', area: 'Misfortunes, evils, hidden afflictions' },
  { divisor: 40, name: 'Khavedamsa',     short: 'D40', area: 'Auspicious & inauspicious — maternal lineage' },
  { divisor: 45, name: 'Akshavedamsa',   short: 'D45', area: 'Character, paternal lineage, conduct' },
  { divisor: 60, name: 'Shashtiamsa',    short: 'D60', area: 'Overall result, finest signal of past karma' },
];

/**
 * Builds a complete divisional chart (lagna, planets, grid) for the given Shodashavarga divisor.
 * Output shape matches the D1 chart so the same RasiChart component can render it.
 */
export function buildDivisionalChart(chart, divisor) {
  const dPlanets = {};
  for (const [name, p] of Object.entries(chart.planets)) {
    const si = getDivisionalSign(p.longitude, divisor);
    dPlanets[name] = {
      ...p,
      signIndex: si,
      sign: SIGNS[si],
      signEn: SIGNS_EN[si],
      signLord: SIGN_LORDS[si]
    };
  }
  const dLagSi = getDivisionalSign(chart.lagna.longitude, divisor);
  const dLagna = {
    ...chart.lagna,
    signIndex: dLagSi,
    sign: SIGNS[dLagSi],
    signEn: SIGNS_EN[dLagSi],
    signLord: SIGN_LORDS[dLagSi]
  };
  const dGrid = SOUTH_INDIAN_GRID.map(cell => {
    const planetsInSign = Object.entries(dPlanets)
      .filter(([, p]) => p.signIndex === cell.sign)
      .map(([name, p]) => ({ name, ...p }));
    if (dLagSi === cell.sign) {
      planetsInSign.unshift({ name: 'Lagna', ...dLagna });
    }
    return {
      ...cell,
      signName: SIGNS[cell.sign], signNameEn: SIGNS_EN[cell.sign],
      signAbbr: SIGN_ABBR[cell.sign], signLord: SIGN_LORDS[cell.sign],
      planets: planetsInSign
    };
  });
  return { lagna: dLagna, planets: dPlanets, grid: dGrid };
}

function getNakshatra(lon) {
  const l = norm360(lon);
  const nakSpan = 360 / 27;
  const idx = Math.floor(l / nakSpan);
  const posInNak = l % nakSpan;
  const pada = Math.floor(posInNak / (nakSpan / 4)) + 1;
  return { index: idx, name: NAKSHATRAS[idx], lord: NAKSHATRA_LORDS[idx], pada };
}

// ─── Lahiri Ayanamsa ─────────────────────────────────────────────
// Matches vedic-astrology-api: base 22.46° at 1900, rate 50.26"/yr

function getLahiriAyanamsa(date) {
  const year = date.getFullYear() + (date.getMonth() / 12) + (date.getDate() / 365.25);
  const yearsDiff = year - 1900;
  return 22.46 + (yearsDiff * 50.26 / 3600);
}

// ─── Julian Day ──────────────────────────────────────────────────

function getJulianDay(date) {
  return (date.getTime() / 86400000) + 2440587.5;
}

function jdToDateStr(jd) {
  const z = Math.floor(jd + 0.5);
  let A;
  if (z < 2299161) A = z;
  else {
    const alpha = Math.floor((z - 1867216.25) / 36524.25);
    A = z + 1 + alpha - Math.floor(alpha / 4);
  }
  const B = A + 1524;
  const C = Math.floor((B - 122.1) / 365.25);
  const D = Math.floor(365.25 * C);
  const E = Math.floor((B - D) / 30.6001);
  const day = B - D - Math.floor(30.6001 * E);
  const month = E < 14 ? E - 1 : E - 13;
  const year = month > 2 ? C - 4716 : C - 4715;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addOneDay(dateStr) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function dateToJD(year, month, day, hourUT) {
  let y = year, m = month;
  if (m <= 2) { y -= 1; m += 12; }
  const A = Math.floor(y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (y + 4716)) +
         Math.floor(30.6001 * (m + 1)) +
         day + hourUT / 24 + B - 1524.5;
}

// ─── Planetary Positions (astronomy-engine) ──────────────────────

function getGeocentricEclipticLon(body, utDate) {
  if (body === 'Sun') {
    return Astronomy.SunPosition(utDate).elon;
  }
  if (body === 'Moon') {
    return norm360(Astronomy.EclipticGeoMoon(utDate).lon);
  }
  const geo = Astronomy.GeoVector(body, utDate, true);
  const ecl = Astronomy.Ecliptic(geo);
  return ecl.elon;
}

function isRetrograde(body, utDate) {
  if (body === 'Sun' || body === 'Moon') return false;
  const before = new Date(utDate.getTime() - 86400000);
  const after = new Date(utDate.getTime() + 86400000);
  let diff = getGeocentricEclipticLon(body, after) - getGeocentricEclipticLon(body, before);
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return diff < 0;
}

// ─── Rahu/Ketu (Lunar Nodes) ─────────────────────────────────────
// High-precision formula from vedic-astrology-api (VSOP87/DE405 based)

function calculateNutationInLongitude(T) {
  const omega = norm360(125.04452 - 1934.136261 * T);
  const L = norm360(280.4665 + 36000.7698 * T);
  const Lp = norm360(218.3165 + 481267.8813 * T);
  return (-17.20 * Math.sin(omega * Math.PI / 180)
          - 1.32 * Math.sin(2 * L * Math.PI / 180)
          - 0.23 * Math.sin(2 * Lp * Math.PI / 180)
          + 0.21 * Math.sin(2 * omega * Math.PI / 180)) / 3600;
}

function calculateLunarNodes(date, ayanamsa) {
  const jd = getJulianDay(date);
  const T = (jd - 2451545.0) / 36525.0;

  let L = 125.0445479 - 1934.1362891 * T + 0.0020754 * T * T
          + T * T * T / 467441.0 - T * T * T * T / 60616000.0;

  const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T * T + T * T * T / 545868.0;
  const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T * T + T * T * T / 24490000.0;
  const MP = 134.9633964 + 477198.8675055 * T + 0.0087414 * T * T + T * T * T / 69699.0;
  const F = 93.2720950 + 483202.0175233 * T - 0.0036539 * T * T;

  const toRad = d => d * Math.PI / 180;
  const corrections = [
    { c: -1.274, a: toRad(MP - 2 * D) },
    { c: 0.658, a: toRad(-2 * D) },
    { c: -0.186, a: toRad(M) },
    { c: -0.059, a: toRad(2 * MP - 2 * D) },
    { c: -0.057, a: toRad(MP - 2 * D + M) },
    { c: 0.053, a: toRad(MP + 2 * D) },
    { c: 0.046, a: toRad(2 * D - M) },
    { c: 0.041, a: toRad(MP - M) },
    { c: -0.035, a: toRad(D) },
    { c: -0.031, a: toRad(MP + M) }
  ];

  for (const { c, a } of corrections) {
    L += c * Math.sin(a) / 3600;
  }

  L = norm360(L);
  L += calculateNutationInLongitude(T);

  let rahuSid = norm360(L - ayanamsa);
  let ketuSid = norm360(rahuSid + 180);

  return {
    Rahu: { longitude: rahuSid },
    Ketu: { longitude: ketuSid }
  };
}

// ─── Ascendant (Lagna) ───────────────────────────────────────────
// Same formula as vedic-astrology-api

function calculateAscendant(date, latitude, longitude, ayanamsa) {
  const jd = getJulianDay(date);
  const gmst = (18.697374558 + 24.06570982441908 * (jd - 2451545.0)) % 24;
  const lst = (gmst + longitude / 15) % 24;

  const latRad = latitude * Math.PI / 180;
  const lstRad = lst * Math.PI / 12;
  const oblRad = 23.43929111 * Math.PI / 180;

  const ascRad = Math.atan2(
    Math.cos(lstRad),
    -Math.sin(lstRad) * Math.cos(oblRad) - Math.tan(latRad) * Math.sin(oblRad)
  );

  let asc = ascRad * 180 / Math.PI;
  if (asc < 0) asc += 360;
  asc -= ayanamsa;
  if (asc < 0) asc += 360;

  return asc;
}

// ─── Vimshottari Dasha ───────────────────────────────────────────

function calculateDasha(moonSiderealLon, birthJd) {
  const nak = getNakshatra(moonSiderealLon);
  const lord = nak.lord;
  const nakSpan = 360 / 27;
  const posInNak = (norm360(moonSiderealLon) % nakSpan) / nakSpan;
  const balanceYears = DASHA_YEARS[lord] * (1 - posInNak);
  const lordIdx = DASHA_SEQUENCE.indexOf(lord);

  const mahadashas = [];
  let currentJd = birthJd;

  for (let i = 0; i < 9; i++) {
    const idx = (lordIdx + i) % 9;
    const planet = DASHA_SEQUENCE[idx];
    const years = i === 0 ? balanceYears : DASHA_YEARS[planet];
    const endJd = currentJd + years * 365.25;
    const antardashas = calculateAntardasha(planet, currentJd, years, endJd);

    mahadashas.push({
      planet, years: round2(years),
      startJd: currentJd, endJd,
      startDate: i === 0 ? jdToDateStr(currentJd) : addOneDay(jdToDateStr(currentJd)),
      endDate: jdToDateStr(endJd),
      isBalance: i === 0,
      antardashas
    });
    currentJd = endJd;
  }

  const birthMaha = mahadashas[0];
  const birthAntar = birthMaha.antardashas[0] || null;

  return {
    nakshatra: nak.name,
    nakshatraIndex: nak.index,
    nakshatraPada: nak.pada,
    nakshatraLord: lord,
    balanceOfDasha: round2(balanceYears),
    birthDasha: {
      mahadasha: birthMaha.planet,
      antardasha: birthAntar ? birthAntar.planet : birthMaha.planet,
      mahadashaEnd: birthMaha.endDate,
      antardashaEnd: birthAntar ? birthAntar.endDate : birthMaha.endDate
    },
    mahadashas
  };
}

/**
 * Calculate sub-periods at any level.
 * Level 1: Antardasha (within Mahadasha)
 * Level 2: Pratyantardasha (within Antardasha)
 * Level 3: Sookshma dasha (within Pratyantardasha)
 *
 * Formula: sub-period duration = (parent lord years × sub lord years) / 120
 * Then scaled to the parent's actual duration.
 */
export function calculateSubPeriods(parentLord, startJd, parentDurationYears, parentEndJd) {
  const parentIdx = DASHA_SEQUENCE.indexOf(parentLord);
  const fullParentYears = DASHA_YEARS[parentLord];
  // Derive exact duration from JDs when available — caller-passed years may be rounded,
  // and that rounding compounds across recursive levels (Antar → Pratyantar → Sookshma → Prana).
  const exactDuration = parentEndJd !== undefined
    ? (parentEndJd - startJd) / 365.25
    : parentDurationYears;
  const periods = [];
  let currentJd = startJd;

  for (let i = 0; i < 9; i++) {
    const idx = (parentIdx + i) % 9;
    const planet = DASHA_SEQUENCE[idx];
    const subYears = (DASHA_YEARS[parentLord] * DASHA_YEARS[planet]) / 120;
    const scaledYears = subYears * (exactDuration / fullParentYears);
    // Clamp last sub-period to parent's exact endJd to absorb any FP drift.
    const endJd = (i === 8 && parentEndJd !== undefined)
      ? parentEndJd
      : currentJd + scaledYears * 365.25;

    periods.push({
      planet, years: round2(scaledYears),
      months: round2(scaledYears * 12),
      days: Math.round(scaledYears * 365.25),
      startJd: currentJd, endJd,
      startDate: i === 0 ? jdToDateStr(currentJd) : addOneDay(jdToDateStr(currentJd)),
      endDate: jdToDateStr(endJd)
    });
    currentJd = endJd;
  }
  return periods;
}

// Backward compat alias
function calculateAntardasha(mahaLord, startJd, mahaDurationYears, mahaEndJd) {
  return calculateSubPeriods(mahaLord, startJd, mahaDurationYears, mahaEndJd);
}

/**
 * Find the currently running dasha at all 4 levels for a given date.
 * Returns: { mahadasha, antardasha, pratyantardasha, sookshmaDasha }
 * Each level has: { planet, startDate, endDate, startJd, endJd }
 */
export function getCurrentDashaBhukti(mahadashas, targetDate) {
  const targetJd = (targetDate instanceof Date)
    ? (targetDate.getTime() / 86400000) + 2440587.5
    : targetDate; // allow passing JD directly

  const result = { mahadasha: null, antardasha: null, pratyantardasha: null, sookshmaDasha: null, pranaDasha: null };

  // Level 1: Mahadasha
  const maha = mahadashas.find(m => targetJd >= m.startJd && targetJd < m.endJd);
  if (!maha) return result;
  result.mahadasha = { planet: maha.planet, startDate: maha.startDate, endDate: maha.endDate, startJd: maha.startJd, endJd: maha.endJd };

  // Level 2: Antardasha
  const antars = maha.antardashas || calculateSubPeriods(maha.planet, maha.startJd, maha.years, maha.endJd);
  const antar = antars.find(a => targetJd >= a.startJd && targetJd < a.endJd);
  if (!antar) return result;
  result.antardasha = { planet: antar.planet, startDate: antar.startDate, endDate: antar.endDate, startJd: antar.startJd, endJd: antar.endJd };

  // Level 3: Pratyantardasha
  const pratyantars = calculateSubPeriods(antar.planet, antar.startJd, antar.years, antar.endJd);
  const pratyantar = pratyantars.find(p => targetJd >= p.startJd && targetJd < p.endJd);
  if (!pratyantar) return result;
  result.pratyantardasha = { planet: pratyantar.planet, startDate: pratyantar.startDate, endDate: pratyantar.endDate, startJd: pratyantar.startJd, endJd: pratyantar.endJd };

  // Level 4: Sookshma Dasha
  const sookshmas = calculateSubPeriods(pratyantar.planet, pratyantar.startJd, pratyantar.years, pratyantar.endJd);
  const sookshma = sookshmas.find(s => targetJd >= s.startJd && targetJd < s.endJd);
  if (!sookshma) return result;
  result.sookshmaDasha = { planet: sookshma.planet, startDate: sookshma.startDate, endDate: sookshma.endDate, startJd: sookshma.startJd, endJd: sookshma.endJd };

  // Level 5: Prana Dasha (finest level — hours/days)
  const pranas = calculateSubPeriods(sookshma.planet, sookshma.startJd, sookshma.years, sookshma.endJd);
  const prana = pranas.find(p => targetJd >= p.startJd && targetJd < p.endJd);
  if (!prana) return result;
  result.pranaDasha = { planet: prana.planet, startDate: prana.startDate, endDate: prana.endDate, startJd: prana.startJd, endJd: prana.endJd };

  return result;
}

// ─── Gochara (Current Transits) ──────────────────────────────────

export function getCurrentTransits() {
  const now = new Date();
  const ayanamsa = getLahiriAyanamsa(now);
  const toSid = (trop) => norm360(trop - ayanamsa);

  const bodies = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
  const transits = {};

  for (const body of bodies) {
    const tropLon = getGeocentricEclipticLon(body, now);
    const sidLon = toSid(tropLon);
    const si = getSignIndex(sidLon);
    const nak = getNakshatra(sidLon);
    transits[body] = {
      longitude: round2(sidLon),
      sign: SIGNS[si], signEn: SIGNS_EN[si], signIndex: si,
      degree: round2(sidLon % 30),
      nakshatra: nak.name, pada: nak.pada,
      symbol: PLANET_SYMBOLS[body]
    };
  }

  // Rahu/Ketu
  const nodes = calculateLunarNodes(now, ayanamsa);
  for (const node of ['Rahu', 'Ketu']) {
    const lon = nodes[node].longitude;
    const si = getSignIndex(lon);
    const nak = getNakshatra(lon);
    transits[node] = {
      longitude: round2(lon),
      sign: SIGNS[si], signEn: SIGNS_EN[si], signIndex: si,
      degree: round2(lon % 30),
      nakshatra: nak.name, pada: nak.pada,
      symbol: PLANET_SYMBOLS[node]
    };
  }

  return transits;
}

// ─── Main Chart Generation (browser-side) ─────────────────────────

export function generateChart({ year, month, day, hour, minute, latitude, longitude, timezone = 5.5 }) {
  // Convert local time to UT
  const localHour = hour + minute / 60;
  const utHour = localHour - timezone;

  let utYear = year, utMonth = month, utDay = day, utH = utHour;
  if (utH < 0) {
    utH += 24; utDay -= 1;
    if (utDay < 1) { utMonth -= 1; if (utMonth < 1) { utMonth = 12; utYear -= 1; } utDay = new Date(utYear, utMonth, 0).getDate(); }
  } else if (utH >= 24) {
    utH -= 24; utDay += 1;
    const dim = new Date(utYear, utMonth, 0).getDate();
    if (utDay > dim) { utDay = 1; utMonth += 1; if (utMonth > 12) { utMonth = 1; utYear += 1; } }
  }

  const utDate = new Date(Date.UTC(utYear, utMonth - 1, utDay,
    Math.floor(utH), Math.round((utH % 1) * 60)));
  const jd = dateToJD(utYear, utMonth, utDay, utH);

  // Ayanamsa
  const ayanamsa = getLahiriAyanamsa(utDate);
  const toSid = (trop) => norm360(trop - ayanamsa);

  // Planetary positions via astronomy-engine
  const tropPositions = {};
  const bodies = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
  for (const body of bodies) {
    tropPositions[body] = getGeocentricEclipticLon(body, utDate);
  }

  // Rahu & Ketu
  const nodes = calculateLunarNodes(utDate, ayanamsa);

  // Retrograde detection
  const retrograde = {
    Sun: false, Moon: false,
    Mars: isRetrograde('Mars', utDate),
    Mercury: isRetrograde('Mercury', utDate),
    Jupiter: isRetrograde('Jupiter', utDate),
    Venus: isRetrograde('Venus', utDate),
    Saturn: isRetrograde('Saturn', utDate),
    Rahu: true, Ketu: true,
  };

  // Ascendant
  const ascLon = calculateAscendant(utDate, latitude, longitude, ayanamsa);

  // Build planet data (sidereal)
  const planets = {};
  for (const body of bodies) {
    const sidLon = toSid(tropPositions[body]);
    const si = getSignIndex(sidLon);
    const nak = getNakshatra(sidLon);
    planets[body] = {
      longitude: round2(sidLon),
      sign: SIGNS[si], signEn: SIGNS_EN[si], signIndex: si, signLord: SIGN_LORDS[si],
      degree: round2(sidLon % 30),
      nakshatra: nak.name, nakshatraLord: nak.lord, pada: nak.pada,
      symbol: PLANET_SYMBOLS[body],
      isRetrograde: retrograde[body] || false
    };
  }

  // Rahu & Ketu
  for (const node of ['Rahu', 'Ketu']) {
    const lon = nodes[node].longitude;
    const si = getSignIndex(lon);
    const nak = getNakshatra(lon);
    planets[node] = {
      longitude: round2(lon),
      sign: SIGNS[si], signEn: SIGNS_EN[si], signIndex: si, signLord: SIGN_LORDS[si],
      degree: round2(lon % 30),
      nakshatra: nak.name, nakshatraLord: nak.lord, pada: nak.pada,
      symbol: PLANET_SYMBOLS[node],
      isRetrograde: true
    };
  }

  // Lagna
  const lagSi = getSignIndex(ascLon);
  const lagNak = getNakshatra(ascLon);
  const lagna = {
    longitude: round2(ascLon),
    sign: SIGNS[lagSi], signEn: SIGNS_EN[lagSi], signIndex: lagSi, signLord: SIGN_LORDS[lagSi],
    degree: round2(ascLon % 30),
    nakshatra: lagNak.name, nakshatraLord: lagNak.lord, pada: lagNak.pada,
    symbol: 'Lg'
  };

  // Rashi (Moon sign)
  const moonSi = planets.Moon.signIndex;
  const rashi = {
    sign: SIGNS[moonSi], signEn: SIGNS_EN[moonSi], signLord: SIGN_LORDS[moonSi],
    nakshatra: planets.Moon.nakshatra, nakshatraLord: planets.Moon.nakshatraLord,
    pada: planets.Moon.pada, degree: planets.Moon.degree
  };

  // Dasha
  const dasha = calculateDasha(planets.Moon.longitude, jd);

  // South Indian chart grid
  const grid = SOUTH_INDIAN_GRID.map(cell => {
    const planetsInSign = Object.entries(planets)
      .filter(([, p]) => p.signIndex === cell.sign)
      .map(([name, p]) => ({ name, ...p }));
    if (lagSi === cell.sign) {
      planetsInSign.unshift({ name: 'Lagna', ...lagna });
    }
    return {
      ...cell,
      signName: SIGNS[cell.sign], signNameEn: SIGNS_EN[cell.sign],
      signAbbr: SIGN_ABBR[cell.sign], signLord: SIGN_LORDS[cell.sign],
      planets: planetsInSign
    };
  });

  // ── Houses (Bhavas) — house 1 starts at Lagna sign ──
  const houses = Array.from({length: 12}, (_, i) => {
    const houseSignIdx = (lagSi + i) % 12;
    const birthPlanets = Object.entries(planets)
      .filter(([, p]) => p.signIndex === houseSignIdx)
      .map(([name]) => name);
    return {
      house: i + 1,
      signIndex: houseSignIdx,
      sign: SIGNS[houseSignIdx],
      signEn: SIGNS_EN[houseSignIdx],
      lord: SIGN_LORDS[houseSignIdx],
      birthPlanets
    };
  });

  return {
    input: { year, month, day, hour, minute, latitude, longitude, timezone },
    ayanamsa: round2(ayanamsa),
    lagna, rashi, planets, dasha, grid, houses,
    signs: SIGNS, signsEn: SIGNS_EN, signAbbr: SIGN_ABBR
  };
}
